import React from 'react';
import { useCurrency } from '../hooks/useCurrency';

interface CurrencyDisplayProps {
  amount: number;
  currency: 'USD' | 'GEL';
  showConversionInfo?: boolean;
  className?: string;
}

interface CurrencySelectProps {
  amount: number;
  currency: 'USD' | 'GEL';
  onCurrencySelect: (selectedCurrency: 'USD' | 'GEL', selectedAmount: number) => void;
  selectedCurrency?: 'USD' | 'GEL';
}

/**
 * Display price with automatic GEL conversion for Georgian users
 */
export const CurrencyDisplay: React.FC<CurrencyDisplayProps> = ({
  amount,
  currency,
  showConversionInfo = false,
  className = ''
}) => {
  const { currencyData, loading, error, isGeorgianUser } = useCurrency(amount, currency);

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-6 bg-stitch-border/40 rounded w-20"></div>
      </div>
    );
  }

  if (error || !currencyData) {
    return (
      <span className={className}>
        {currency === 'USD' ? '$' : '₾'}{amount}
      </span>
    );
  }

  const displayPrice = currencyData.formatDisplay();
  const originalPrice = currencyData.formatOriginal();

  return (
    <div className={className}>
      {/* Main price display */}
      <span className="font-semibold text-lg">
        {displayPrice}
      </span>
      
      {/* Show conversion info for Georgian users */}
      {isGeorgianUser && showConversionInfo && currencyData.conversionData && (
        <div className="text-sm text-stitch-muted mt-1">
          <div className="flex items-center gap-1">
            <span>≈ {originalPrice}</span>
            <span className="rounded bg-stitch-info-soft px-1 text-xs text-stitch-info dark:text-stitch-info">
              {currencyData.conversionData.exchangeRate.toFixed(3)} rate
            </span>
          </div>
          <div className="text-xs text-stitch-muted">
            Live exchange rate • Updated hourly
          </div>
        </div>
      )}
      
      {/* Georgia flag for Georgian users */}
      {isGeorgianUser && (
        <span className="ml-2 text-sm">🇬🇪</span>
      )}
    </div>
  );
};

/**
 * Currency selection component for Georgian users
 */
export const CurrencySelect: React.FC<CurrencySelectProps> = ({
  amount,
  currency,
  onCurrencySelect,
  selectedCurrency
}) => {
  const { currencyData, loading, isGeorgianUser, hasMultiplePaymentOptions } = useCurrency(amount, currency);

  // Don't show selection if not Georgian or loading
  if (!isGeorgianUser || !hasMultiplePaymentOptions || loading || !currencyData) {
    return null;
  }

  const handleCurrencyChange = (optionCurrency: 'USD' | 'GEL', optionAmount: number) => {
    onCurrencySelect(optionCurrency, optionAmount);
  };

  return (
    <div className="mt-4 rounded-stitch-lg border border-stitch-border bg-stitch-primary-soft/50 p-4 dark:bg-stitch-surface-2">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-lg">🇬🇪</span>
        <h3 className="font-medium text-stitch-fg">Choose your payment currency</h3>
      </div>
      
      <div className="space-y-3">
        {currencyData.paymentOptions.map((option) => (
          <label
            key={option.currency}
            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              selectedCurrency === option.currency
                ? 'border-stitch-primary bg-stitch-primary-soft/80'
                : 'border-stitch-border hover:border-stitch-primary/30'
            }`}
          >
            <input
              type="radio"
              name="payment-currency"
              value={option.currency}
              checked={selectedCurrency === option.currency}
              onChange={() => handleCurrencyChange(option.currency, option.amount)}
              className="text-stitch-primary focus:ring-stitch-ring"
            />
            
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{option.label}</span>
                {option.isRecommended && (
                  <span className="rounded-full bg-stitch-success-soft px-2 py-1 text-xs font-medium text-stitch-success dark:text-stitch-success">
                    Recommended
                  </span>
                )}
              </div>
              
              {option.currency === 'GEL' && currency === 'USD' && currencyData.conversionData && (
                <div className="text-sm text-stitch-muted mt-1">
                  Converted from {currencyData.formatOriginal()} at rate {currencyData.conversionData.exchangeRate.toFixed(3)}
                </div>
              )}
            </div>
          </label>
        ))}
      </div>
      
      <div className="text-xs text-stitch-muted mt-3 text-center">
        Exchange rates are updated hourly. Final amount will be charged in selected currency.
      </div>
    </div>
  );
};

/**
 * Simple price badge component
 */
export const PriceBadge: React.FC<CurrencyDisplayProps> = ({ amount, currency, className = '' }) => {
  const { formatPrice, loading } = useCurrency(amount, currency);

  if (loading) {
    return <div className={`animate-pulse bg-stitch-border/40 rounded px-2 py-1 w-16 ${className}`}></div>;
  }

  return (
    <span className={`rounded-full bg-stitch-primary-soft px-2 py-1 text-sm font-medium text-stitch-primary ${className}`}>
      {formatPrice()}
    </span>
  );
};
